import apiClient from '../config/axiosConfig';
import { API_BASE_URL } from '../utils/apiUtils';

const validateUserId = (userId) => {
  if (!userId || typeof userId !== 'string' || userId.trim() === '') {
    throw new Error('User ID is required and must be a valid string');
  }

  const idRegex = /^[a-zA-Z0-9-_]+$/;
  if (!idRegex.test(userId.trim())) {
    throw new Error('User ID must contain only letters, numbers, hyphens, and underscores');
  }

  return userId.trim();
};

const validateProfilePictureUrl = (profilePictureUrl) => {
  if (!profilePictureUrl || typeof profilePictureUrl !== 'string' || profilePictureUrl.trim() === '') {
    throw new Error('Profile picture URL is required and must be a valid string');
  }

  try {
    new URL(profilePictureUrl.trim());
  } catch (error) {
    throw new Error('Invalid profile picture URL format provided');
  }

  return profilePictureUrl.trim();
};

const validateProfileSlug = (profileSlug) => {
  if (!profileSlug || typeof profileSlug !== 'string' || profileSlug.trim() === '') {
    throw new Error('Profile slug is required and must be a valid string');
  }

  const slugRegex = /^[a-zA-Z0-9-_]+$/;
  if (!slugRegex.test(profileSlug.trim())) {
    throw new Error('Profile slug must contain only letters, numbers, hyphens, and underscores');
  }

  if (profileSlug.trim().length < 3 || profileSlug.trim().length > 50) {
    throw new Error('Profile slug must be between 3 and 50 characters long');
  }

  return profileSlug.trim().toLowerCase();
};

const validateSlug = (slug) => {
  if (!slug || typeof slug !== 'string' || slug.trim() === '') {
    throw new Error('Slug is required and must be a valid string');
  }

  const slugRegex = /^[a-zA-Z0-9-_]+$/;
  if (!slugRegex.test(slug.trim())) {
    throw new Error('Slug must contain only letters, numbers, hyphens, and underscores');
  }

  return slug.trim().toLowerCase();
};

const validatePage = (page) => {
  if (page !== null && page !== undefined) {
    const pageNum = parseInt(page, 10);
    if (isNaN(pageNum) || pageNum < 1) {
      throw new Error('Page must be a positive integer');
    }
    return pageNum;
  }
  return 1;
};

const validateLimit = (limit) => {
  if (limit !== null && limit !== undefined) {
    const limitNum = parseInt(limit, 10);
    if (isNaN(limitNum) || limitNum < 1 || limitNum > 100) {
      throw new Error('Limit must be a positive integer between 1 and 100');
    }
    return limitNum;
  }
  return 50;
};

const handleUserApiError = (error, operation = 'User API operation') => {
  if (error.response?.status === 401) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    throw new Error('Your session has expired. Please log in again');
  }

  if (error.response?.status === 403) {
    throw new Error('You do not have permission to access user data');
  }

  if (error.response?.status === 404) {
    throw new Error('User not found or no longer available');
  }

  if (error.response?.status === 409) {
    throw new Error('Profile slug already exists or conflicts with existing data');
  }

  if (error.response?.status === 422) {
    const validationErrors = error.response?.data?.errors || [];
    const errorMessage = validationErrors.length > 0
      ? `Validation errors: ${validationErrors.join(', ')}`
      : 'Invalid user data provided';
    throw new Error(errorMessage);
  }

  if (error.response?.status >= 500) {
    throw new Error('User service error. Please try again later.');
  }

  if (error.code === 'ERR_NETWORK') {
    throw new Error('Network error. Please check your connection and try again.');
  }

  if (error.code === 'ECONNABORTED') {
    throw new Error('Request timeout. Please try again.');
  }

  const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
  throw new Error(errorMessage);
};

export const getCardsByUser = async (userId) => {
  try {
    const validUserId = validateUserId(userId);

    let lastError = null;
    const maxRetries = 1;
    const baseDelay = 1000;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await apiClient.get(`${API_BASE_URL}/api/user/${validUserId}`);
        return response.data;
      } catch (error) {
        lastError = error;

        if (attempt === maxRetries) {
          break;
        }

        const delay = baseDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    handleUserApiError(lastError, 'Getting cards by user');
  } catch (error) {
    handleUserApiError(error, 'Getting cards by user');
  }
};

export const updateUserProfilePicture = async (userId, profilePictureUrl) => {
  try {
    const validUserId = validateUserId(userId);
    const validProfilePictureUrl = validateProfilePictureUrl(profilePictureUrl);

    const response = await apiClient.put(
      `${API_BASE_URL}/api/user/update-profile-picture/${validUserId}`,
      { profilePictureUrl: validProfilePictureUrl }
    );

    return response.data;
  } catch (error) {
    handleUserApiError(error, 'Updating user profile picture');
  }
};

export const updateProfileSlug = async (userId, profileSlug) => {
  try {
    const validUserId = validateUserId(userId);
    const validProfileSlug = validateProfileSlug(profileSlug);

    const response = await apiClient.put(
      `${API_BASE_URL}/api/user/update-profile-slug/${validUserId}`,
      { profileSlug: validProfileSlug }
    );

    return response.data;
  } catch (error) {
    handleUserApiError(error, 'Updating profile slug');
  }
};

export const getUserBySlug = async (slug) => {
  try {
    const validSlug = validateSlug(slug);

    const response = await apiClient.get(`${API_BASE_URL}/api/user/by-slug/${validSlug}`);

    return response.data;
  } catch (error) {
    handleUserApiError(error, 'Getting user by slug');
  }
};

export const getUserProfile = async (userId) => {
  try {
    const validUserId = validateUserId(userId);

    const response = await apiClient.get(`${API_BASE_URL}/api/user/profile/${validUserId}`);

    return response.data;
  } catch (error) {
    handleUserApiError(error, 'Getting user profile');
  }
};

export const updateUserProfile = async (userId, profileData = {}) => {
  try {
    const validUserId = validateUserId(userId);

    const validProfileData = {};

    if (profileData.firstName !== undefined) {
      if (typeof profileData.firstName !== 'string' || profileData.firstName.trim() === '') {
        throw new Error('First name must be a non-empty string');
      }
      validProfileData.firstName = profileData.firstName.trim();
    }

    if (profileData.lastName !== undefined) {
      if (typeof profileData.lastName !== 'string' || profileData.lastName.trim() === '') {
        throw new Error('Last name must be a non-empty string');
      }
      validProfileData.lastName = profileData.lastName.trim();
    }

    if (profileData.email !== undefined) {
      if (typeof profileData.email !== 'string' || profileData.email.trim() === '') {
        throw new Error('Email must be a non-empty string');
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(profileData.email.trim())) {
        throw new Error('Invalid email format provided');
      }
      validProfileData.email = profileData.email.trim().toLowerCase();
    }

    if (profileData.phone !== undefined) {
      if (profileData.phone && typeof profileData.phone !== 'string') {
        throw new Error('Phone must be a string');
      }
      validProfileData.phone = profileData.phone ? profileData.phone.trim() : '';
    }

    if (profileData.company !== undefined) {
      if (profileData.company && typeof profileData.company !== 'string') {
        throw new Error('Company must be a string');
      }
      validProfileData.company = profileData.company ? profileData.company.trim() : '';
    }

    const response = await apiClient.put(
      `${API_BASE_URL}/api/user/profile/${validUserId}`,
      validProfileData
    );

    return response.data;
  } catch (error) {
    handleUserApiError(error, 'Updating user profile');
  }
};

export const getAllUsers = async (page = 1, limit = 50) => {
  try {
    const validPage = validatePage(page);
    const validLimit = validateLimit(limit);

    const response = await apiClient.get(
      `${API_BASE_URL}/api/user/all?page=${validPage}&limit=${validLimit}`
    );

    return response.data;
  } catch (error) {
    handleUserApiError(error, 'Getting all users');
  }
};

export const deleteUserAccount = async (userId) => {
  try {
    const validUserId = validateUserId(userId);

    const response = await apiClient.delete(
      `${API_BASE_URL}/api/user/delete/${validUserId}`
    );

    return response.data;
  } catch (error) {
    handleUserApiError(error, 'Deleting user account');
  }
};

export const checkProfileSlugAvailability = async (profileSlug) => {
  try {
    const validProfileSlug = validateProfileSlug(profileSlug);

    const response = await apiClient.get(
      `${API_BASE_URL}/api/user/check-slug/${validProfileSlug}`
    );

    return response.data;
  } catch (error) {
    handleUserApiError(error, 'Checking profile slug availability');
  }
};
